import { useSession } from "@core/hooks/useSession";
import { getInfinitePostsQueryKey } from "@features/community-v2/hooks/useInfinitePosts";
import { useUserFeedSettings } from "@features/community-v2/hooks/useUserFeedSettings";
import MenuIcon from "@heroicons/react/solid/MenuIcon";
import XIcon from "@heroicons/react/solid/XIcon";
import cn from "classnames";
import Image from "next/image";
import Link, { LinkProps } from "next/link";
import { Router, useRouter } from "next/router";
import {
  forwardRef,
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQueryClient } from "react-query";

import { NavigationLink } from "./types";

interface NextLinkProps {
  children?: ReactNode;
  href: LinkProps["href"];
  className?: string;
}

const NextLink = forwardRef<HTMLAnchorElement, NextLinkProps>((props, ref) => {
  const { href, children, ...rest } = props;

  return (
    <Link href={href} passHref>
      <a {...rest} ref={ref}>
        {children}
      </a>
    </Link>
  );
});

NextLink.displayName = "NextLink";

interface MobileMenuProps {
  callToAction: ReactNode;
  isTherapyMember: boolean;
  isWebView: boolean;
  navigationLinks: NavigationLink[];
  openSubMenuId: string | null;
  setOpenSubMenuId: (id: string | null) => void;
}

export default function MobileMenu({
  callToAction,
  isTherapyMember,
  isWebView,
  navigationLinks,
  openSubMenuId,
  setOpenSubMenuId,
}: MobileMenuProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  const router = useRouter();
  const { data: session } = useSession();
  const queryClient = useQueryClient();

  const { feedSettingsByUser } = useUserFeedSettings();
  const userFeedSettings = feedSettingsByUser?.[session?.user?.id];
  const infinitePostsQueryKey = getInfinitePostsQueryKey(
    session?.accessToken,
    userFeedSettings
  );

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Close the menu on route change
  useEffect(() => {
    Router.events.on("routeChangeComplete", closeMenu);

    return () => {
      Router.events.off("routeChangeComplete", closeMenu);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prevent body scroll when opened
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.style.overflow = isOpen ? "hidden" : "scroll";
    }
  }, [isOpen]);

  const subMenu = useMemo(
    () => navigationLinks.find((link) => link.id === openSubMenuId)?.subMenu,
    [openSubMenuId, navigationLinks]
  );

  const subMenuTitle = useMemo(
    () => navigationLinks.find((link) => link.id === openSubMenuId)?.title,
    [openSubMenuId, navigationLinks]
  );

  return (
    <nav className="tablet:hidden">
      <header className="flex h-[58px] max-w-[1072px] items-center justify-between px-4">
        <div className="flex flex-1 start">
          <button
            type="button"
            className="h-6 w-21"
            onClick={async () => {
              // We embed this web app in our mobile apps under a "member portal"
              // tab. When the app is accessed inside of a web view, the user
              // should not be able to navigate to the community. If the user is
              // a therapy member, their "homepage" should be therapy.
              if (isWebView || isTherapyMember) {
                return router.push("/therapy", undefined, { scroll: true });
              }

              await router.push("/community/posts", undefined, {
                scroll: true,
              });

              // The user is on the home page and they click the logo in the
              // navbar, refresh the main feed.
              if (router.route === "/community/posts") {
                await queryClient.invalidateQueries([infinitePostsQueryKey]);
              }

              return undefined;
            }}
          >
            <Image
              src="/images/nocd-logo.svg"
              alt="Go to homepage"
              width={84}
              height={24}
              priority
            />
          </button>
        </div>

        <nav>{callToAction}</nav>

        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="focus:outline-none inline-flex items-center justify-center rounded-md p-2 text-gray-800 transition-colors hover:bg-indigo-100 hover:text-indigo-600 focus:ring-2 focus:ring-inset focus:ring-indigo-600"
        >
          <span className="sr-only">Open main menu</span>
          {isOpen ? (
            <XIcon
              className="block h-6 w-6 transition-colors"
              aria-hidden="true"
            />
          ) : (
            <MenuIcon
              className="block h-6 w-6 transition-colors"
              aria-hidden="true"
            />
          )}
        </button>
      </header>

      {isOpen && (
        <div className="font-manrope font-bold top-[58px] overflow-y-scroll left-0 bottom-0 right-0 bg-[#F0F1FF] fixed space-y-1 px-4 pt-2 pb-3">
          {!openSubMenuId
            ? navigationLinks
                .filter(({ hide }) => !hide)
                .map(({ href, id, title, mobileComponent }) => (
                  <Fragment key={id}>
                    {mobileComponent || (
                      <NextLink
                        href={href}
                        className={cn(
                          "block rounded-md px-3 py-2 text-base transition-colors text-left",
                          href === router.pathname && "bg-[#E3E4FF]"
                        )}
                      >
                        <div>{title}</div>
                      </NextLink>
                    )}
                  </Fragment>
                ))
            : null}

          {subMenu && subMenuTitle ? (
            <div className="mb-4 mt-2">
              <button
                type="button"
                onClick={() => setOpenSubMenuId(null)}
                className="text-indigo-600 flex items-center space-x-2"
              >
                <span>
                  <svg
                    width="13"
                    height="10"
                    viewBox="0 0 13 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.55 4.675L0.549987 4.675M0.549987 4.675L4.67499 8.8M0.549987 4.675L4.67499 0.55"
                      stroke="#545CEA"
                      stroke-width="1.1"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
                <span className="font-bold">{subMenuTitle}</span>
              </button>

              <div className="mt-4">{subMenu}</div>
            </div>
          ) : null}
        </div>
      )}
    </nav>
  );
}
